import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, Checkbox } from '@morressier/alembic';
import { FormProvider, useForm } from 'react-hook-form';
import _ from 'lodash';
import {
  FormFieldDefinition,
  FormFieldCheckboxArray
} from '@publishing-engine/domain/types/article';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { generateValidationSchema } from '../../../../utils/validation';
import FormFieldTextArea from 'src/components/FormField/FormFieldTextArea';
import { IFormFieldText } from '../Text';

interface Props {
  isOpen: boolean;
  onSubmit: (value: string[], dataAvailabilityText?: string) => void;
  onCancel: () => void;
  formField: FormFieldCheckboxArray<'preference.dataAvailabilityStatement'>;
  value: string[];
  dataAvailabilityTextConfig?: IFormFieldText | null;
  dataAvailabilityTextValue?: string;
}

type ICheckboxFormState = {
  [key: string]: string[] | string;
};

const DataAvailabilityStatementUpdateModal: React.FC<Props> = props => {
  const {
    isOpen,
    onSubmit,
    onCancel,
    formField,
    dataAvailabilityTextConfig,
    dataAvailabilityTextValue
  } = props;

  const defaultValues = _.set(
    {} as ICheckboxFormState,
    formField.caseDataFields.main,
    props.value
  );

  const fieldDefs: Record<string, FormFieldDefinition> = {
    [formField.name]: formField
  };

  if (dataAvailabilityTextConfig) {
    _.set(
      defaultValues,
      dataAvailabilityTextConfig.caseDataFields.main,
      dataAvailabilityTextValue ?? ''
    );
    fieldDefs[dataAvailabilityTextConfig.name] = dataAvailabilityTextConfig;
  }

  const schema = generateValidationSchema(fieldDefs);

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const { handleSubmit, watch, setValue, clearErrors } = methods;

  const fieldName = formField.caseDataFields.main;
  const mainField = `${fieldName}` as const;
  const fieldValue: string[] = watch(mainField) || [];

  const handleCheckboxChange = (checked: boolean, itemValue: string) => {
    clearErrors(mainField);

    const EXCLUSIVE_VALUES = ['no_new_data', 'no_data_available'];

    let newValue: string[] = [];

    if (checked) {
      if (EXCLUSIVE_VALUES.includes(itemValue)) {
        newValue = [itemValue];
      } else {
        newValue = [
          ...fieldValue.filter(v => !EXCLUSIVE_VALUES.includes(v)),
          itemValue
        ];
      }
    } else {
      newValue = fieldValue.filter(v => v !== itemValue);
    }

    setValue(mainField, newValue as never, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  const options = Object.keys(formField.params.labels.options).map(
    optionKey => ({
      value: optionKey,
      children: (
        <Flex flexDirection="column">
          <Text textStyle="textBody">
            <strong>
              {formField.params.labels.options[optionKey]?.main.replace(
                /\.$/,
                ''
              )}
              :
            </strong>{' '}
            {formField.params.labels.options[optionKey]?.secondary}
          </Text>
        </Flex>
      )
    })
  );

  const handleFormSubmit = (data: ICheckboxFormState) => {
    const checkboxValue = _.get(data, fieldName) as string[];
    const textValue = dataAvailabilityTextConfig
      ? (_.get(
          data,
          dataAvailabilityTextConfig.caseDataFields.main
        ) as string)
      : undefined;
    onSubmit(checkboxValue, textValue);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      width="755px"
      closeButtonProps={{ hidden: true }}
    >
      <Flex
        padding={8}
        gap={4}
        bgColor="surfacePrimaryFill"
        flexDirection="column"
        borderRadius="base"
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Flex marginBottom={4}>
              <Text textStyle="headingSection">{`Update ${formField.params.labels.main}`}</Text>
            </Flex>

            <Flex
              padding={8}
              gap={6}
              bgColor="surfacePrimaryFill"
              flexDirection="column"
              borderRadius="base"
              flex={1}
            >
              <Flex gap={4} flexDirection="column" marginBottom={4}>
                {options.map(opt => (
                  <Checkbox
                    key={opt.value}
                    id={opt.value}
                    name={mainField}
                    ariaLabel={mainField}
                    isChecked={fieldValue.includes(opt.value)}
                    onChange={({ target: { checked } }) =>
                      handleCheckboxChange(checked, opt.value)
                    }
                  >
                    {opt.children}
                  </Checkbox>
                ))}
              </Flex>

              {dataAvailabilityTextConfig && (
                <FormFieldTextArea
                  config={dataAvailabilityTextConfig}
                  placeholder="Provide any additional details"
                />
              )}
            </Flex>

            <Flex
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf="stretch"
            >
              <Button
                variant="secondary"
                onClick={onCancel}
                borderRadius="small"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" borderRadius="small">
                Save Changes
              </Button>
            </Flex>
          </form>
        </FormProvider>
      </Flex>
    </Modal>
  );
};

export default DataAvailabilityStatementUpdateModal;
